import { useSelector, useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  fetchScheduledBookings,
  createScheduledBooking,
  updateScheduledBooking,
  cancelScheduledBooking,
  setActiveScheduledBooking,
  clearActiveScheduledBooking,
  addResourceAssignment,
  removeResourceAssignment,
  updateResourceAssignment,
  clearScheduledBookingError,
} from "../actions/ScheduledBookingActions";

export const useScheduledBookings = () => {
  const dispatch = useDispatch();

  const bookings = useSelector((state) => state.scheduledBooking.items);
  const activeBooking = useSelector((state) => state.scheduledBooking.activeBooking);
  const loading = useSelector((state) => state.scheduledBooking.loading);
  const error = useSelector((state) => state.scheduledBooking.error);
  const cancellingMap = useSelector((state) => state.scheduledBooking.cancellingMap);

  const loadBookings = useCallback(() => {
    return dispatch(fetchScheduledBookings());
  }, [dispatch]);

  const create = useCallback(
    (bookingData) => {
      return dispatch(createScheduledBooking(bookingData));
    },
    [dispatch]
  );

  const update = useCallback(
    (scheduledBookingId, updatedData) => {
      return dispatch(updateScheduledBooking(scheduledBookingId, updatedData));
    },
    [dispatch]
  );

  const cancel = useCallback(
    (scheduledBookingId) => {
      return dispatch(cancelScheduledBooking(scheduledBookingId));
    },
    [dispatch]
  );

  const setActive = useCallback(
    (booking) => {
      dispatch(setActiveScheduledBooking(booking));
    },
    [dispatch]
  );

  const clearActive = useCallback(() => {
    dispatch(clearActiveScheduledBooking());
  }, [dispatch]);

  const addResource = useCallback(
    (resource) => {
      dispatch(addResourceAssignment(resource));
    },
    [dispatch]
  );

  const removeResource = useCallback(
    (resourceId) => {
      dispatch(removeResourceAssignment(resourceId));
    },
    [dispatch]
  );

  const updateResource = useCallback(
    (resource) => {
      dispatch(updateResourceAssignment(resource));
    },
    [dispatch]
  );

  const clearError = useCallback(() => {
    dispatch(clearScheduledBookingError());
  }, [dispatch]);

  const getBookingsByStatus = useCallback(
    (status) => {
      if (!status || status === "All") return bookings;
      return bookings.filter((b) => b.status === status);
    },
    [bookings]
  );

  const getUpcomingBookings = useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    return bookings.filter(
      (b) => b.bookedDate >= today && b.status !== "Cancelled"
    );
  }, [bookings]);

  const getCompletedBookings = useCallback(() => {
    return bookings.filter((b) => b.status === "Completed");
  }, [bookings]);

  const getCancelledBookings = useCallback(() => {
    return bookings.filter((b) => b.status === "Cancelled");
  }, [bookings]);

  const isTimeSlotAvailable = useCallback(
    (vehicleId, bookedDate, startTime, endTime) => {
      return !bookings.some((b) => {
        const statusRaw =
          b.status ?? b.Status ?? b.bookingStatus ?? b.booking_status ?? "";
        const status = String(statusRaw).trim().toLowerCase();
        const hasCompletedDate = Boolean(b.completed_date || b.completedDate || b.completedDateTime);
        if (
          
          b.bookedDate !== bookedDate ||
          status === "cancelled" ||
          status === "canceled" ||
          status === "completed" ||
          status === "complete" ||
          status === "co" ||
          hasCompletedDate
        ) {
          return false;
        }
        const bStart = parseInt(b.startTime.replace(":", ""));
        const bEnd = parseInt(b.endTime.replace(":", ""));
        const sTime = parseInt(startTime.replace(":", ""));
        const eTime = parseInt(endTime.replace(":", ""));
        return !(eTime <= bStart || sTime >= bEnd);
      });
    },
    [bookings]
  );

  return {
    bookings,
    activeBooking,
    loading,
    error,
    cancellingMap,
    loadBookings,
    create,
    update,
    cancel,
    setActive,
    clearActive,
    addResource,
    removeResource,
    updateResource,
    clearError,
    getBookingsByStatus,
    getUpcomingBookings,
    getCompletedBookings,
    getCancelledBookings,
    isTimeSlotAvailable,
  };
};
